import React from 'react'
import icons from 'payments-icons-library'
import { PaymentModeType } from '../types/merchantAttributes'
import {
  BannerConfig,
  CouponVisibilityConfig,
  PaymentModeUiConfig,
  PaymentModeUiConfigBE,
  PaymentPageConfig,
  PaymentPageConfigBEType,
  PaymentTileConfig,
  UpiIntentConfig
} from '../types/paymentPage'
import { SubscriptionConfig, SubscriptionTileMode } from '../types/subscriptions'
import { PaymentPageState } from '../contexts/PaymentPageContext'
import { regexPatterns } from './validations'
import { PaymentMode, AdditionalChargeType, PaymentModeScope } from '../types/paymentSettings'
import { UPI_INTENT_APPS as apps } from '@shopflo/ui'

export const HIDDEN_PAYMENT_MODES = [
  'UPI_COLLECT',
  'GYFTR',
  'REWARDS_WALLET',
  'LOYALTY',
  'FREE',
  'GIFTCARD',
  'SIMPL',
  'SIMPL_PL'
]

const paymentModeConfigs: {
  [key: string]: {
    type: 'Component' | 'Image'
    component?: string
    path?: string
    header: string
    subheader: string
  }
} = {
  UPI_COLLECT: {
    type: 'Component',
    component: 'UPI',
    header: 'Pay via UPI Collect',
    subheader: 'Any UPI app or UPI ID'
  },
  UPI: {
    type: 'Component',
    component: 'UPI',
    header: 'Pay via UPI',
    subheader: 'Enter your UPI ID'
  },
  UPI_INTENT: {
    type: 'Component',
    component: 'UPI',
    header: 'Pay via UPI Intent',
    subheader: 'Quick pay through UPI apps'
  },
  CARD: {
    type: 'Component',
    component: 'CARD',
    header: 'Debit/Credit cards',
    subheader: 'Visa, Mastercard, RuPay'
  },
  COD: {
    type: 'Component',
    component: 'COD',
    header: 'Cash on delivery',
    subheader: 'Pay in cash at delivery'
  },
  WALLET: {
    type: 'Component',
    component: 'WALLET',
    header: 'Wallets',
    subheader: 'PhonePe, PayZapp, PayPal'
  },
  NETBANKING: {
    type: 'Component',
    component: 'NETBANKING',
    header: 'Netbanking',
    subheader: 'Choose from leading banks'
  },
  EMI: {
    type: 'Component',
    component: 'EMI',
    header: 'EMIs',
    subheader: 'Convert bills into EMIs'
  },
  PAY_LATER: {
    type: 'Component',
    component: 'PAY_LATER',
    header: 'Pay later',
    subheader: 'Buy now, clear dues later'
  },
  SNPM: {
    type: 'Image',
    header: 'Snapmint',
    subheader: '0% EMI • No card required'
  },
  VELOCITY: {
    type: 'Image',
    header: 'Velocity',
    subheader: 'Pay in 4 installments'
  },
  BHARATX: {
    type: 'Component',
    component: 'CARD',
    header: 'BharatX',
    subheader: '0% EMI • Card not needed'
  },
  SPLIT_COD: {
    type: 'Component',
    component: 'COD',
    header: 'Partial COD',
    subheader: 'Pay part online, rest later'
  },
  CRED: {
    type: 'Component',
    component: 'CARD',
    header: 'CRED Pay',
    subheader: 'Use saved cards on CRED'
  },
  CASHFREE: {
    type: 'Component',
    component: 'CARD',
    header: 'Pay online',
    subheader: 'UPI, Cards, EMI, Wallets'
  },
  GC: {
    type: 'Component',
    component: 'CARD',
    header: 'Use gift card',
    subheader: 'Redeem card balance'
  },
  PL: {
    type: 'Component',
    component: 'CARD',
    header: 'Pay Online',
    subheader: 'Cards, UPI, Netbanking'
  },
  LOYALTY: {
    type: 'Component',
    component: 'CARD',
    header: 'Redeem store credits',
    subheader: 'Use loyalty points'
  },
  PAYPAL: {
    type: 'Component',
    component: 'WALLET',
    header: 'PayPal',
    subheader: 'For global transactions'
  },
  OFFLINE_PAYMENT: {
    type: 'Component',
    component: 'CARD',
    header: 'Pay online',
    subheader: 'UPI, Wallets, Cards, EMI'
  }
}

export const parsePaymentConfig = (
  data: PaymentPageConfigBEType,
  paymentModes: PaymentModeType[]
): PaymentPageConfig => {
  const bannerConfigs =
    data?.ui_attributes?.banner_config?.map(banner => ({
      type: banner.type,
      enabled: banner.enabled,
      htmlTextString: banner.html_text_string,
      backgroundColor: banner.background_color,
      textColor: banner.text_color
    })) ?? []

  const couponVisibilityConfigs =
    data?.ui_attributes?.coupon_visibility_config?.map(coupon => ({
      hideSavings: coupon?.hide_savings,
      visibilityExtent: coupon?.visibility_extent,
      areCouponsVisible: coupon?.visibile ?? false,
      type: coupon?.type
    })) ?? []

  const sub = data?.ui_attributes?.subscription_config
  const subLayout = sub?.layout_ui
  const subscriptionConfig: SubscriptionConfig = {
    layoutUi: {
      subscriptionCard: {
        backgroundColor: subLayout?.subscription_card?.background_color || SUBSCRIPTION_CARD_DEFAULTS.backgroundColor,
        textColor: subLayout?.subscription_card?.text_color || SUBSCRIPTION_CARD_DEFAULTS.textColor,
        iconColor: subLayout?.subscription_card?.icon_color || SUBSCRIPTION_CARD_DEFAULTS.iconColor,
        offerColor: subLayout?.subscription_card?.offer_color || SUBSCRIPTION_CARD_DEFAULTS.offerColor,
        highlightBackgroundColor:
          subLayout?.subscription_card?.highlight_background_color ||
          SUBSCRIPTION_CARD_DEFAULTS.highlightBackgroundColor,
        highlightTextColor:
          subLayout?.subscription_card?.highlight_text_color || SUBSCRIPTION_CARD_DEFAULTS.highlightTextColor
      },
      tabNudge: subLayout?.tab_nudge ?? DEFAULT_TAB_NUDGE,
      recommendedLabel: subLayout?.recommended_label ?? DEFAULT_RECOMMENDED_LABEL,
      summaryTitles: subLayout?.summary_titles ?? DEFAULT_SUBSCRIPTION_SUMMARY_TITLES
    },
    paymentTiles: subscriptionTileDefaults(paymentModes).map(fallback => {
      const saved = sub?.payment_tiles?.find(tile => tile?.mode === fallback.mode)
      if (!saved) return fallback
      return {
        ...fallback,
        ...saved,
        weight: fallback.weight,
        title: saved.title || fallback.title,
        subtitle: saved.subtitle || fallback.subtitle,
        suggested_tile: saved.suggested_tile ?? fallback.suggested_tile
      }
    })
  }

  const layoutUiBackgroundColor =
    data?.ui_attributes?.payment_tile_config?.layout_ui?.payment_card?.background_color ?? '#FFFFFF'

  const layoutUi = {
    paymentCard: {
      backgroundColor: layoutUiBackgroundColor,
      borderColor: data?.ui_attributes?.payment_tile_config?.layout_ui?.payment_card?.border_color ?? '#F0F0F0',
      textColor: data?.ui_attributes?.payment_tile_config?.layout_ui?.payment_card?.text_color ?? '#4D4D4D',
      iconColor: data?.ui_attributes?.payment_tile_config?.layout_ui?.payment_card?.icon_color ?? '#4D4D4D',
      subtitleColor: data?.ui_attributes?.payment_tile_config?.layout_ui?.payment_card?.subtitle_color ?? '#949494',
      borderRadius:
        data?.ui_attributes?.payment_tile_config?.layout_ui?.payment_card?.border_radius ?? PAYMENT_CARD_RADIUS
    },
    offerBadge: {
      backgroundColor: data?.ui_attributes?.payment_tile_config?.layout_ui?.offer_badge?.background_color ?? '#F3F9F3',
      textColor: data?.ui_attributes?.payment_tile_config?.layout_ui?.offer_badge?.text_color ?? '#2c874a'
    },
    paymentTileGap: data?.ui_attributes?.payment_tile_config?.layout_ui?.payment_tile_gap ?? '8px'
  }

  const activeModes = new Set(paymentModes.filter(isCheckoutMode).map(mode => mode.mode))

  const omittedModeDetails = (data?.ui_attributes?.payment_tile_config?.mode_details ?? []).filter(
    mode => !activeModes.has(mode.mode)
  )

  const paymentTileConfigs = {
    layout: data?.ui_attributes?.payment_tile_config?.layout ?? 'ICONS',
    layoutUi: layoutUi,
    modeDetails: normalizeWeights(
      parsePaymentTileUiConfig(data?.ui_attributes?.payment_tile_config?.mode_details, paymentModes, layoutUi)
    ),
    omittedModeDetails
  }

  return {
    bannerConfigs,
    couponVisibilityConfigs,
    paymentTileConfigs,
    subscriptionConfig
  }
}

export const PAYMENT_CARD_RADIUS = 16

export const SUBSCRIPTION_CARD_DEFAULTS = {
  backgroundColor: '#FFFFFF',
  textColor: '#4D4D4D',
  iconColor: '#949494',
  offerColor: '#2C874A',
  highlightBackgroundColor: '#009966',
  highlightTextColor: '#2C874A'
}

export const DEFAULT_TAB_NUDGE = 'More savings!'
export const DEFAULT_RECOMMENDED_LABEL = 'Recommended'

export const DEFAULT_SUBSCRIPTION_SUMMARY_TITLES = [
  'First order will be placed {date_of_order}',
  'Orders will be placed every {frequency} \u00b7 Next on {upcoming_order}',
  'Subscription ends on {duration_date} \u00b7 {delivery_count} deliveries total',
]

export const SUBSCRIPTION_SUMMARY_MIN_LINES = 1

const subscriptionTile = (mode: PaymentModeType): PaymentModeUiConfigBE => ({
  mode: mode.mode,
  title: mode.metadata?.title || mode.title || '',
  subtitle: mode.metadata?.description || '',
  payment_card: {
    background_color: '',
    border_color: '',
    text_color: '',
    icon_color: '',
    subtitle_color: '',
    is_subtitle_hidden: false
  },
  offer_badge: { background_color: '', text_color: '' },
  is_suggested: false,
  suggested_tile: { background_color: '', text_color: '', title: '' },
  icons: [],
  weight: mode.weight
})

export const subscriptionTileDefaults = (paymentModes: PaymentModeType[]): PaymentModeUiConfigBE[] =>
  (paymentModes ?? [])
    .filter(mode => mode.purchase_type === 'SUBSCRIPTION' && !HIDDEN_PAYMENT_MODES.includes(mode.mode))
    .sort((a, b) => a.weight - b.weight)
    .map(subscriptionTile)

export const defaultSubscriptionConfig: SubscriptionConfig = {
  layoutUi: {
    subscriptionCard: { ...SUBSCRIPTION_CARD_DEFAULTS },
    tabNudge: DEFAULT_TAB_NUDGE,
    recommendedLabel: DEFAULT_RECOMMENDED_LABEL,
    summaryTitles: DEFAULT_SUBSCRIPTION_SUMMARY_TITLES
  },
  paymentTiles: []
}

export const defaultPaymentConfig: PaymentPageConfig = {
  bannerConfigs: [],
  couponVisibilityConfigs: [],
  subscriptionConfig: defaultSubscriptionConfig,
  paymentTileConfigs: {
    layout: 'ICONS',
    layoutUi: {
      paymentCard: {
        backgroundColor: '#FFFFFF',
        borderColor: '#F0F0F0',
        textColor: '#4D4D4D',
        iconColor: '#4D4D4D',
        subtitleColor: '#949494',
        borderRadius: PAYMENT_CARD_RADIUS
      },
      offerBadge: {
        backgroundColor: '',
        textColor: ''
      },
      paymentTileGap: '8px'
    },
    modeDetails: [],
    omittedModeDetails: []
  }
}

export const defaultBannerConfig: BannerConfig = {
  type: 'PAYMENTS',
  enabled: false,
  htmlTextString: '',
  backgroundColor: '',
  textColor: ''
}

export const defaultCouponVisibilityConfig: CouponVisibilityConfig = {
  hideSavings: false,
  visibilityExtent: 'cards',
  areCouponsVisible: false,
  type: 'PAYMENTS'
}

const DEFAULTUPIINTENTAPPS: Pick<UpiIntentConfig, 'other_apps' | 'selected_apps'> = apps

const isCheckoutMode = (mode: PaymentModeType) =>
  (mode.purchase_type ?? 'CHECKOUT') === 'CHECKOUT' && !HIDDEN_PAYMENT_MODES.includes(mode.mode)

export const parsePaymentTileUiConfig = (
  data: PaymentModeUiConfigBE[],
  paymentModes: PaymentModeType[],
  layoutUi: PaymentPageConfig['paymentTileConfigs']['layoutUi']
): PaymentModeUiConfig[] => {
  const filteredPaymentModes = paymentModes.filter(isCheckoutMode).sort((a, b) => a.weight - b.weight)

  const parsedData = filteredPaymentModes.map(mode => {
    const modeData = data?.find(item => item?.mode === mode.mode)

    const defaultConfig = paymentModeConfigs[mode.mode] || {
      type: 'Component',
      component: 'CARD',
      path: '/images/payments/card.svg',
      header: '',
      subheader: ''
    }

    const imgPath = {
      type: defaultConfig.type,
      component: defaultConfig.component,
      path: defaultConfig.path
    }

    // For fixed-image modes (VELOCITY, SNPM), skip library resolution entirely.
    // getDefaultIcons returns 'velocity_icon' / 'snapmint' which the library may
    // resolve to a wrong icon (e.g. Amex). Always use the canonical image URL and
    // respect an explicitly empty saved state (user deselected).
    let resolvedIcons: Array<{ name: string; url: string }>
    if (paymentMethodImages[mode.mode]) {
      const savedIcons = modeData?.icons
      if (Array.isArray(savedIcons)) {
        resolvedIcons = savedIcons.length > 0 ? [{ name: mode.mode, url: paymentMethodImages[mode.mode] }] : []
      } else {
        resolvedIcons = [{ name: mode.mode, url: paymentMethodImages[mode.mode] }]
      }
    } else {
      const rawIcons = modeData?.icons ?? getDefaultIcons(mode.mode)
      resolvedIcons = resolvePaymentIcons(rawIcons || [])
    }

    return {
      mode: mode?.mode ?? '',
      modeTitle: mode.title ?? '',
      title: modeData?.title || mode.metadata.title || mode.title || defaultConfig.header,
      subtitle: modeData?.subtitle || mode.metadata.description || defaultConfig.subheader,
      paymentCard: {
        backgroundColor: modeData?.payment_card?.background_color ?? '',
        borderColor: modeData?.payment_card?.border_color ?? '',
        textColor: modeData?.payment_card?.text_color ?? '',
        iconColor: modeData?.payment_card?.icon_color ?? '',
        subtitleColor: modeData?.payment_card?.subtitle_color ?? '',
        isSubtitleHidden: modeData?.payment_card?.is_subtitle_hidden ?? mode.metadata.hideDescription ?? false
      },
      offerBadge: {
        backgroundColor: modeData?.offer_badge?.background_color ?? '',
        textColor: modeData?.offer_badge?.text_color ?? ''
      },
      isSuggested: modeData?.is_suggested ?? false,
      suggestedTile: {
        backgroundColor: modeData?.suggested_tile?.background_color ?? '#000000',
        textColor: modeData?.suggested_tile?.text_color ?? '#FFFFFF',
        title: modeData?.suggested_tile?.title ?? 'Suggested payment method'
      },
      modeConfig:
        mode.mode === 'UPI_INTENT'
          ? modeData?.mode_config
            ? {
                ...modeData.mode_config,
                selected_apps:
                  modeData.mode_config?.selected_apps?.length > 0
                    ? modeData.mode_config?.selected_apps
                    : DEFAULTUPIINTENTAPPS.selected_apps,
                other_apps:
                  modeData.mode_config?.other_apps?.length > 0
                    ? modeData.mode_config?.other_apps
                    : DEFAULTUPIINTENTAPPS.other_apps,
                is_s2s_verified: modeData.mode_config.is_s2s_verified ?? false
              }
            : { ...DEFAULTUPIINTENTAPPS, is_s2s_verified: Boolean(mode.weight > 0) }
          : undefined,
      icons: resolvedIcons,
      imgPath: imgPath,
      weight: modeData?.weight ?? mode.weight
    }
  })

  return parsedData
}

export const createPaymentUiAttributesPayload = (state: PaymentPageState) => {
  const subscription = state.checkoutCustomizationData.subscriptionConfig
  const bannerConfig = state.checkoutCustomizationData.bannerConfigs.map(banner => ({
    type: banner.type,
    enabled: banner.enabled,
    html_text_string: banner.htmlTextString,
    background_color: banner.backgroundColor,
    text_color: banner.textColor
  }))

  const couponVisibilityConfig = state.checkoutCustomizationData.couponVisibilityConfigs.map(coupon => ({
    hide_savings: coupon.hideSavings,
    visibility_extent: coupon.visibilityExtent,
    visibile: coupon.areCouponsVisible,
    type: coupon.type
  }))

  return [
    {
      channel: 'web',
      sf_context: 'CHECKOUT',
      config: {
        type: 'CHECKOUT',
        banner_config: bannerConfig,
        coupon_visibility_config: couponVisibilityConfig,
        payment_tile_config: {
          layout: state.checkoutCustomizationData.paymentTileConfigs.layout,
          layout_ui: {
            payment_card: {
              background_color: state.checkoutCustomizationData.paymentTileConfigs.layoutUi.paymentCard.backgroundColor,
              border_color: state.checkoutCustomizationData.paymentTileConfigs.layoutUi.paymentCard.borderColor,
              text_color: state.checkoutCustomizationData.paymentTileConfigs.layoutUi.paymentCard.textColor,
              icon_color: state.checkoutCustomizationData.paymentTileConfigs.layoutUi.paymentCard.iconColor,
              subtitle_color: state.checkoutCustomizationData.paymentTileConfigs.layoutUi.paymentCard.subtitleColor,
              border_radius: state.checkoutCustomizationData.paymentTileConfigs.layoutUi.paymentCard.borderRadius
            },
            offer_badge: {
              background_color: state.checkoutCustomizationData.paymentTileConfigs.layoutUi.offerBadge.backgroundColor,
              text_color: state.checkoutCustomizationData.paymentTileConfigs.layoutUi.offerBadge.textColor
            },
            payment_tile_gap: state.checkoutCustomizationData.paymentTileConfigs.layoutUi.paymentTileGap || '8px'
          },
          mode_details: state.checkoutCustomizationData.paymentTileConfigs.modeDetails.map(mode => ({
            mode: mode.mode,
            title: mode.title,
            ...(mode.modeConfig != null && { mode_config: mode.modeConfig }),
            subtitle: mode.subtitle,
            payment_card: {
              background_color: mode.paymentCard.backgroundColor,
              border_color: mode.paymentCard.borderColor,
              text_color: mode.paymentCard.textColor,
              icon_color: mode.paymentCard.iconColor,
              subtitle_color: mode.paymentCard.subtitleColor,
              is_subtitle_hidden: mode.paymentCard.isSubtitleHidden
            },
            offer_badge: {
              background_color: mode.offerBadge.backgroundColor,
              text_color: mode.offerBadge.textColor
            },
            is_suggested: mode.isSuggested,
            suggested_tile: {
              background_color: mode.suggestedTile.backgroundColor,
              text_color: mode.suggestedTile.textColor,
              title: mode.suggestedTile.title
            },
            icons: mode.icons
          }))
        },
        subscription_config: {
          layout_ui: {
            subscription_card: {
              background_color: subscription.layoutUi.subscriptionCard.backgroundColor,
              text_color: subscription.layoutUi.subscriptionCard.textColor,
              icon_color: subscription.layoutUi.subscriptionCard.iconColor,
              offer_color: subscription.layoutUi.subscriptionCard.offerColor,
              highlight_background_color: subscription.layoutUi.subscriptionCard.highlightBackgroundColor,
              highlight_text_color: subscription.layoutUi.subscriptionCard.highlightTextColor
            },
            tab_nudge: subscription.layoutUi.tabNudge,
            recommended_label: subscription.layoutUi.recommendedLabel,
            summary_titles: subscription.layoutUi.summaryTitles
          },
          payment_tiles: subscription.paymentTiles
        }
      }
    }
  ]
}

export const createMerchantAttributesPayload = (state: PaymentPageState) => {
  return {
    attributes: {
      ui: {
        banner_config: state.checkoutCustomizationData.bannerConfigs.map(banner => ({
          type: banner.type,
          enabled: banner.enabled,
          html_text_string: banner.htmlTextString,
          background_color: banner.backgroundColor,
          text_color: banner.textColor
        })),
        coupon_visibility_config: state.checkoutCustomizationData.couponVisibilityConfigs.map(coupon => ({
          hide_savings: coupon.hideSavings,
          visibility_extent: coupon.visibilityExtent,
          visibile: coupon.areCouponsVisible,
          type: coupon.type
        }))
      }
    }
  }
}

export const createPaymentModesPayload = (
  modeDetails: PaymentModeUiConfig[],
  subscriptionTiles: PaymentModeUiConfigBE[] = []
) => {
  const checkoutModes = modeDetails
    .sort((a, b) => a.weight - b.weight)
    .map(mode => ({
      mode: mode.mode,
      weight: mode.weight,
      purchase_type: 'CHECKOUT' as PaymentModeScope,
      metadata: {
        title: mode.title,
        description: mode.subtitle,
        hideDescription: !mode.subtitle || mode.subtitle.trim() === '',
        ...(mode.modeConfig && { mode_config: mode.modeConfig })
      }
    }))

  const subscriptionModes = [...subscriptionTiles]
    .sort((a, b) => a.weight - b.weight)
    .map(tile => ({
      mode: tile.mode,
      weight: tile.weight,
      purchase_type: 'SUBSCRIPTION' as PaymentModeScope,
      metadata: {
        title: tile.title,
        description: tile.subtitle,
        hideDescription: !tile.subtitle || tile.subtitle.trim() === '',
        ...(tile.mode_config && { mode_config: tile.mode_config })
      }
    }))

  return { modes: [...checkoutModes, ...subscriptionModes] }
}

export type PaymentType = 'cards' | 'wallets' | 'upi' | 'netbanking'

export const PAYMENT_TYPE_TO_MODES: Record<PaymentType, string[]> = {
  cards: ['CARD', 'EMI'],
  wallets: ['WALLET', 'PAY_LATER', 'PL'],
  upi: ['UPI', 'UPI_INTENT', 'UPI_COLLECT'],
  netbanking: ['NETBANKING', 'BHARATX']
}

const MODE_TO_PAYMENT_TYPE: Record<string, PaymentType> = Object.entries(PAYMENT_TYPE_TO_MODES).reduce(
  (acc, [type, modes]) => {
    modes.forEach(m => {
      acc[m] = type as PaymentType
    })
    return acc
  },
  {} as Record<string, PaymentType>
)

export const getPaymentTypeFromMode = (mode: string): PaymentType => {
  return MODE_TO_PAYMENT_TYPE[mode] ?? 'cards'
}

// Single source of truth for modes that always use a fixed logo URL.
// Imported by PaymentMethodIcon.tsx and LogoComponent.tsx.
export const paymentMethodImages: Record<string, string> = {
  SNPM: 'https://cashfreelogo.cashfree.com/assets_images/pg/cardless/64/snapmint.png',
  VELOCITY: 'https://d2s4qtdvyggozf.cloudfront.net/velocity.png'
}

export const getDefaultIcons = (mode: string) => {
  const MODE_DEFAULTS: Record<string, string[]> = {
    SNPM: ['snapmint'],
    VELOCITY: ['velocity_icon'],
    CARD: ['VISA', 'MASTERCARD', 'RUPAY'],
    EMI: [''],
    WALLET: [''],
    PL: ['PHONEPE', 'PAYTM', 'AMAZONPAY'],
    PAY_LATER: ['LAZYPAY', 'ZESTMONEY', 'RINGPAY'],
    UPI: ['PHONEPE', 'GPAY', 'PAYTM'],
    NETBANKING: [''],
    BHARATX: ['BHARATX'],
    COD: [],
    SPLIT_COD: []
  }

  return MODE_DEFAULTS[mode] ?? ['PAYTM', 'PHONEPE', 'GPAY']
}

export const resolvePaymentIcons = (
  iconData: string[] | Array<{ name: string; url: string }>
): Array<{ name: string; url: string }> => {
  const iconObjects = iconData.map(iconItem => {
    if (typeof iconItem === 'string') {
      return { name: iconItem, url: iconItem }
    }
    return iconItem
  })

  return iconObjects
    .map(iconObject => {
      const isUrl = regexPatterns.isUrl.test(iconObject.url)
      if (isUrl) return { name: iconObject.name, url: iconObject.url }

      const iconData = icons.getIcon(iconObject.url.toLowerCase(), 'sm')
      return {
        name: iconData?.icon_name || iconObject.name,
        url: iconData?.icon_url ?? ''
      }
    })
    .filter(Boolean) as Array<{ name: string; url: string }>
}

export const normalizeWeights = (modeDetails: PaymentModeUiConfig[]): PaymentModeUiConfig[] => {
  const enabled = modeDetails.filter(mode => mode.weight > 0)
  const disabled = modeDetails.filter(mode => mode.weight === 0)

  const orderedEnabled = [...enabled].sort((a, b) =>
    a.weight !== b.weight ? a.weight - b.weight : a.mode.localeCompare(b.mode)
  )

  const modeToWeight = new Map<string, number>()
  orderedEnabled.forEach((mode, index) => {
    modeToWeight.set(mode.mode, index + 1)
  })

  const disabledModes = new Set(disabled.map(m => m.mode))

  return modeDetails.map(mode => {
    if (disabledModes.has(mode.mode)) {
      return { ...mode, weight: 0 }
    }
    return {
      ...mode,
      weight: modeToWeight.get(mode.mode) ?? 1
    }
  })
}

export const hasOverriddenColors = (
  paymentCard: PaymentTileConfig['layoutUi']['paymentCard'],
  offerBadge: PaymentTileConfig['layoutUi']['offerBadge']
) => {
  const paymentCardColors = Object.values(paymentCard)
  const offerBadgeColors = Object.values(offerBadge)

  return [...paymentCardColors, ...offerBadgeColors].every(color => color !== '')
}

export const getAdditionalChargePayload = (
  mode: PaymentMode,
  modeToRemove: string,
  scopeToRemove?: PaymentModeScope
) => {
  if (mode.mode === modeToRemove && (!scopeToRemove || (mode.purchase_type ?? 'CHECKOUT') === scopeToRemove)) {
    return {
      additional_charge_type: null,
      additional_charge_value: null
    }
  }
  return {
    additional_charge_type: mode.metadata?.additionalChargeType || null,
    additional_charge_value: mode.metadata?.additionalChargeValue || null
  }
}

export const buildPaymentModePayload = (
  mode: PaymentMode,
  additionalChargePayload?: {
    additional_charge_type: AdditionalChargeType | null
    additional_charge_value: number | null
  }
): PaymentMode => {
  const metadata = mode.metadata || {}
  const chargePayload = additionalChargePayload || {
    additional_charge_type: metadata?.additionalChargeType || null,
    additional_charge_value: metadata?.additionalChargeValue || null
  }

  return {
    mode: mode.mode,
    gateway: mode.gateway || null,
    weight: mode.weight || 0,
    purchase_type: mode.purchase_type ?? 'CHECKOUT',
    metadata: {
      title: metadata?.title || mode.title || mode.mode,
      description: metadata?.description || '',
      hideDescription: metadata?.hideDescription || false,
      ...chargePayload
    }
  }
}
